import { Link, useLocation } from 'react-router';
import { isPlaceholder, legalNav, primaryNav, publicRoutes } from '@ircf/config';
import { useContent } from '../../app/content.js';
import { telHref } from '../../lib/format.js';
import { BrandLockup } from './BrandLockup.js';

/**
 * Public site footer.
 *
 * Notice how much of this is conditional. Contact details, social links and
 * training locations are all still placeholders until the client supplies them,
 * and a footer full of `[BUSINESS PHONE]` looks broken rather than unfinished.
 * So each block renders only when its value is genuinely known; the admin
 * content readiness panel is where the gaps are surfaced instead.
 */
export function PublicFooter() {
  const content = useContent();
  const location = useLocation();
  const showBoxingGymAttribution =
    location.pathname === publicRoutes.boxing.path ||
    location.pathname.endsWith(publicRoutes.boxing.path);
  const showStudioAttribution =
    location.pathname === publicRoutes.personalTraining.path ||
    location.pathname.endsWith(publicRoutes.personalTraining.path);

  const hasEmail = !isPlaceholder(content.business.email);
  const hasPhone = !isPlaceholder(content.business.phone);
  const hasSocials = content.business.socialLinks.length > 0;
  const knownLocations = content.business.locations.filter(
    (location) => !isPlaceholder(location.name),
  );
  const openingHours = content.business.operatingHours.filter(
    (entry) => entry.opens !== null && entry.closes !== null,
  );

  return (
    <footer
      className="relative z-10 border-t border-white/10 bg-near-black"
      data-print-hide="true"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <BrandLockup />
            <p className="max-w-xs text-sm leading-relaxed text-platinum">
              One coach. One standard. Two distinct ways to train.
            </p>
            {hasSocials ? (
              <ul className="flex flex-wrap gap-3 pt-2">
                {content.business.socialLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm text-platinum underline decoration-white/30 underline-offset-4 hover:text-warm-white"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <nav aria-label="Footer" className="space-y-3">
            <h2 className="text-xs font-semibold tracking-[0.28em] text-cool-steel uppercase">
              Explore
            </h2>
            <ul className="space-y-2">
              {primaryNav
                .filter((route) => route.id !== publicRoutes.login.id)
                .map((route) => (
                  <li key={route.id}>
                    <Link
                      to={route.path}
                      className="text-sm text-platinum hover:text-warm-white hover:underline hover:underline-offset-4"
                    >
                      {route.label}
                    </Link>
                  </li>
                ))}
              <li>
                <Link
                  to={publicRoutes.contact.path}
                  className="text-sm text-platinum hover:text-warm-white hover:underline hover:underline-offset-4"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          <div className="space-y-3">
            <h2 className="text-xs font-semibold tracking-[0.28em] text-cool-steel uppercase">
              Get in touch
            </h2>
            <ul className="space-y-2 text-sm text-platinum">
              {hasEmail ? (
                <li>
                  <a
                    href={`mailto:${content.business.email}`}
                    className="hover:text-warm-white hover:underline hover:underline-offset-4"
                  >
                    {content.business.email}
                  </a>
                </li>
              ) : null}
              {hasPhone ? (
                <li>
                  <a
                    href={telHref(content.business.phone)}
                    className="hover:text-warm-white hover:underline hover:underline-offset-4"
                  >
                    {content.business.phone}
                  </a>
                </li>
              ) : null}
              {knownLocations.map((location) => (
                <li key={location.id}>
                  {location.name}
                  {location.suburb ? `, ${location.suburb}` : ''}
                </li>
              ))}
              {!hasEmail && !hasPhone && knownLocations.length === 0 ? (
                <li>
                  <Link
                    to={publicRoutes.contact.path}
                    className="hover:text-warm-white hover:underline hover:underline-offset-4"
                  >
                    Send an enquiry
                  </Link>
                </li>
              ) : null}
            </ul>

            {openingHours.length > 0 ? (
              <div className="pt-2">
                <h3 className="text-xs font-semibold tracking-[0.2em] text-cool-steel uppercase">
                  Hours
                </h3>
                <dl className="mt-2 space-y-1 text-sm text-platinum">
                  {openingHours.map((entry) => (
                    <div key={entry.weekday} className="flex justify-between gap-4">
                      <dt>{entry.label}</dt>
                      <dd className="tabular-nums">
                        {entry.opens} - {entry.closes}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </div>

          <nav aria-label="Legal" className="space-y-3">
            <h2 className="text-xs font-semibold tracking-[0.28em] text-cool-steel uppercase">
              Legal
            </h2>
            <ul className="space-y-2">
              {legalNav.map((route) => (
                <li key={route.id}>
                  <Link
                    to={route.path}
                    className="text-sm text-platinum hover:text-warm-white hover:underline hover:underline-offset-4"
                  >
                    {route.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-cool-steel">
            © {new Date().getFullYear()}{' '}
            {content.business.name}.
            All rights reserved.
          </p>
          <p className="max-w-lg text-xs leading-relaxed text-cool-steel">
            Coaching information on this site is general in nature and is not medical advice. Seek
            clearance from a qualified health professional before beginning a new training or
            nutrition program.
          </p>
        </div>
        {showBoxingGymAttribution ? (
          <p className="mt-4 text-xs leading-relaxed text-cool-steel/80">
            Training gym environment:{' '}
            <a
              href="https://sketchfab.com/3d-models/training-gym-574d0be459164655824740ba2adf37fd"
              className="underline decoration-white/20 underline-offset-2 hover:text-platinum"
              target="_blank"
              rel="noreferrer noopener"
            >
              &ldquo;Training Gym&rdquo;
            </a>{' '}
            by local.yany, via Sketchfab.{' '}
            <a
              href="https://creativecommons.org/licenses/by/4.0/"
              className="underline decoration-white/20 underline-offset-2 hover:text-platinum"
              target="_blank"
              rel="noreferrer noopener"
            >
              Licensed under CC BY 4.0
            </a>
            .
          </p>
        ) : null}
        {showStudioAttribution ? (
          <p className="mt-4 text-xs leading-relaxed text-cool-steel/80">
            Performance studio environment sourced from a Sketchfab gym interior
            (&ldquo;gym basic&rdquo;). Confirm the author credit and licence before production
            publish.
          </p>
        ) : null}
      </div>
    </footer>
  );
}
